import React, { useState } from 'react';
import { Button } from 'antd';
import useSandbox from '../hooks/useSandbox';
import { CONNECTIONS, connectionState } from '../components/sandbox/connections';
import YourAccounts from '../components/accounts/YourAccounts';
import YourAiTools from '../components/sandbox/YourAiTools';
import OrgAiConnections from '../components/sandbox/OrgAiConnections';
import { PlanChangeButton } from '../components/sandbox/PlanRequest';
import '../components/sandbox/sandbox.css';
import './SandboxHome.css';
import './Connections.css';

const STATE_LABEL = {
  included: { text: 'Included in your plan', tone: 'sbx-pill-running' },
  'not-in-plan': { text: 'Not in your plan', tone: '' },
  'org-off': { text: 'Not enabled by your organization', tone: '' },
};

/**
 * Features › Connections (sandbox-first, docs/SANDBOX_FIRST.md): the paid add-ons that bring the rest of Mia-Platform
 * to the sandbox (AI Foundry, Context Catalog, Console, Fast Data), what each gives, and whether the person has it,
 * from the sandbox overview's `connections`. Connecting from Flow is not built yet, and the page says so.
 * Above them, "Your accounts" (components/accounts): the person's own git hosting, package registry and tool
 * accounts (the types of the admin-configured app catalog). First of all, "Your AI tools" (components/sandbox/YourAiTools):
 * the MCP clients connected to the sandbox, and "Connect another". Right after it, for sandbox admins only, "Organization AI
 * connections" (components/sandbox/OrgAiConnections): Microsoft 365 Copilot and Copilot Studio, set up once.
 */
export default function Connections() {
  const { sandbox, loading } = useSandbox({ intervalMs: 60000 });
  const [asked, setAsked] = useState(null);
  const planName = sandbox?.plan?.name;

  return (
    <div className="sbx-home">
      <div className="sbx-home-inner connections">
        <div className="sbx-home-title">
          <span className="sbx-label">Features</span>
          <h1>Connections</h1>
          <p>
            Your AI tools, your own git accounts, and the optional, paid add-ons that bring the rest of Mia-Platform to your sandbox.
          </p>
        </div>

        <YourAiTools loading={loading} sandbox={sandbox} />

        {/* Right after Your AI tools (2026-10-31): the same tools, connected once for everyone (admins only). */}
        <OrgAiConnections />

        <YourAccounts />

        <div className="connections-addons-head">
          <h2>Paid add-ons</h2>
          <p>
            Optional, paid add-ons that bring the rest of Mia-Platform to your sandbox. Your organization’s admin turns
            each one on and your plan includes it; then you connect with your company account. What you connect is also
            offered to your AI tool through the sandbox.
          </p>
        </div>

        <div className="connections-grid">
          {CONNECTIONS.map((item) => {
            const state = connectionState(sandbox, item.key);
            const label = state ? STATE_LABEL[state] : null;
            const headingId = `conn-${item.key}`;
            return (
              <section aria-labelledby={headingId} className="sbx-card connections-card" key={item.key}>
                <div className="connections-card-head">
                  <span className="connections-card-title">
                    <span className="connections-card-name">
                      <strong id={headingId}>{item.name}</strong>
                      <span className="sbx-pill sbx-pill-paid">Paid add-on</span>
                    </span>
                    <span>{item.tagline}</span>
                  </span>
                  {label && <span className={`sbx-pill ${label.tone}`}>{label.text}</span>}
                </div>
                <ul className="connections-gets">
                  {item.gets.map((text) => <li key={text}>{text}</li>)}
                </ul>
                <span className="connections-where"><strong>Shows up in:</strong> {item.where}</span>
                {item.needs && <span className="connections-needs">{item.needs}</span>}
                {state === 'included' && (
                  <>
                    <div className="connections-actions">
                      <Button disabled type="primary">Connect — coming soon</Button>
                    </div>
                    <span className="connections-note">
                      It’s in your plan. Connecting from Flow is still being built; until then your admin can tell you how to use it.
                    </span>
                  </>
                )}
                {state === 'not-in-plan' && (
                  <>
                    <div className="connections-actions">
                      <PlanChangeButton
                        fallback={<Button onClick={() => setAsked(item.key)}>Ask your admin</Button>}
                        label="Ask for a plan with it"
                        size="middle"
                      />
                    </div>
                    {asked === item.key && (
                      <span className="connections-note" role="status">
                        Ask your sandbox admin to add {item.name} to {planName ? `the ${planName} plan` : 'your plan'}.
                      </span>
                    )}
                  </>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
